'use client';

import React from 'react';
import type { ElementType } from 'react';
import type { SeparatorProps as AriaSeparatorProps } from 'react-aria-components';
import { Separator as AriaSeparator } from 'react-aria-components';
import { cx } from '../../../utils/cx';
import type { PolymorphicProps } from '../../../types/polymorphic';

const MENU_SEPARATOR_DISPLAY_NAME = 'RomaMenuSeparator';

type MenuSeparatorOwnProps = {
    className?: string;
};

type MenuSeparatorBaseComponentProps<T extends ElementType> = PolymorphicProps<MenuSeparatorOwnProps, T>;

export type MenuSeparatorBaseProps = PolymorphicProps<MenuSeparatorOwnProps>;

export const MenuSeparatorBase = <T extends ElementType = 'div'>(props: MenuSeparatorBaseComponentProps<T>) => {
    const { as, className, ...restProps } = props;
    const Component = as ?? 'div';

    return (
        <Component
            {...restProps}
            className={cx(
                MENU_SEPARATOR_DISPLAY_NAME,
                // Use the variable to ensure the separator negative margin covers the exact distance of the container padding,
                // otherwise there may be a gap or it could cause a horizontal scroll
                '-mx-(--roma-popover-body-padding-x,var(--spacing-sm))',
                'my-sm block border-0 border-t border-column',
                className,
            )}
        />
    );
};

export type MenuSeparatorProps = Omit<AriaSeparatorProps, 'className' | 'orientation'> & {
    className?: string;
};

export const MenuSeparator: React.FC<MenuSeparatorProps> = ({ className, ...props }) => (
    <MenuSeparatorBase as={AriaSeparator} {...props} orientation="horizontal" className={className} />
);

MenuSeparatorBase.displayName = MENU_SEPARATOR_DISPLAY_NAME;
MenuSeparator.displayName = MENU_SEPARATOR_DISPLAY_NAME;
